import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Alert, Autocomplete, Box, Button, Chip, CircularProgress, Divider,
  FormControl, IconButton, InputAdornment, InputLabel,
  MenuItem, Paper, Select, Skeleton, Snackbar, Switch,
  Accordion, AccordionDetails, AccordionSummary,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination,
  TextField, Tooltip, Typography,
  Dialog, DialogActions, DialogContent, DialogTitle,
  ListItem, ListItemText,
} from '@mui/material';
import {
  Add, ArrowBack, Save, Search, TuneRounded, Refresh, ExpandMore, FileDownloadOutlined,
  CheckCircleOutline, BlockOutlined, EditOutlined, Visibility,
  UploadFileOutlined, CloseRounded, CalendarMonth, InfoOutlined,
  LocalOfferOutlined, HistoryOutlined,
} from '@mui/icons-material';
import { usePermissions } from '../../../../contexts/PermissionContext';
import * as api from '../../../../services/subscriptionAPI';

// ── constants ─────────────────────────────────────────────────────────────────

const SortTH = ({ label, field, sortBy, sortDir, onSort, sx = {} }) => (
  <Box display="flex" alignItems="center" gap={0.5} sx={{ cursor: 'pointer', userSelect: 'none', ...sx }}
    onClick={() => onSort(field)}>
    {label}
    <Box sx={{ display: 'flex', flexDirection: 'column', ml: 0.25, opacity: sortBy === field ? 1 : 0.3 }}>
      <svg width="8" height="5" viewBox="0 0 8 5"><path d="M4 0L8 5H0Z" fill={sortBy === field && sortDir === 'asc' ? 'currentColor' : '#9ca3af'}/></svg>
      <svg width="8" height="5" viewBox="0 0 8 5"><path d="M4 5L0 0H8Z" fill={sortBy === field && sortDir === 'desc' ? 'currentColor' : '#9ca3af'}/></svg>
    </Box>
  </Box>
);

const TH = {
  fontWeight: 700, fontSize: 11, color: 'text.secondary',
  letterSpacing: 0.6, textTransform: 'uppercase', bgcolor: 'grey.50', py: 1.5,
};
const _BTN     = { width: 30, height: 30, borderRadius: 1.5, border: '1px solid', borderColor: 'divider' };
const BTN_PRIMARY = { ..._BTN, color: 'primary.main', '&:hover': { bgcolor: 'primary.50',  borderColor: 'primary.200'  } };
const BTN_SUCCESS = { ..._BTN, color: 'success.main', '&:hover': { bgcolor: 'success.50',  borderColor: 'success.200'  } };
const BTN_ERROR   = { ..._BTN, color: 'error.main',   '&:hover': { bgcolor: 'error.50',    borderColor: 'error.200'    } };

const ACCORDION_SX = {
  border: '1px solid', borderColor: 'divider', borderRadius: '10px !important',
  bgcolor: 'background.paper', boxShadow: 'none', '&:before': { display: 'none' },
  '&.Mui-expanded': { borderColor: 'primary.light' },
};

const NUM_NO_SPIN = {
  '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button':
    { WebkitAppearance: 'none', margin: 0 },
  '& input[type=number]': { MozAppearance: 'textfield' },
};

const DATE_SX = {
  '& input[type="datetime-local"]::-webkit-calendar-picker-indicator': {
    opacity: 0, position: 'absolute', right: 0, top: 0,
    width: '100%', height: '100%', cursor: 'pointer',
  },
};

const DISCOUNT_TYPES = [
  { value: 'percent',      label: 'Percentage (%)' },
  { value: 'fixed_amount', label: 'Fixed Amount' },
  { value: 'token_grant',  label: 'Token Grant' },
];

const EMPTY_FORM = {
  code: '', description: '', discount_type: 'percent', discount_value: '',
  currency_code: 'INR', valid_from: '', valid_to: '',
  max_usage_count: '', per_user_limit: '1', first_invoice_only: false,
  scopes: [], allowed_merchant_ids: [], selectedMerchants: [],
};

// ── helpers ───────────────────────────────────────────────────────────────────

const fmt = (iso) => iso
  ? new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
  : '—';

const fmtDiscount = (c) => {
  if (c.discount_type === 'percent')     return `${c.discount_value}%`;
  if (c.discount_type === 'token_grant') return `${c.discount_value} tokens`;
  return `${c.currency_code || ''} ${c.discount_value}`.trim();
};

function StatusChip({ status }) {
  return status === 1
    ? <Chip size="small" label="Active"   sx={{ fontWeight: 700, fontSize: 11, height: 22, bgcolor: 'success.50', color: 'success.dark', border: '1px solid', borderColor: 'success.200', '& .MuiChip-label': { px: 1 } }} />
    : <Chip size="small" label="Inactive" sx={{ fontWeight: 700, fontSize: 11, height: 22, bgcolor: 'grey.100',   color: 'text.secondary', border: '1px solid', borderColor: 'grey.300',   '& .MuiChip-label': { px: 1 } }} />;
}

// ── MerchantMultiSelect — debounced searchable multi-select ───────────────────
function MerchantMultiSelect({ selected, onChange }) {
  const [inputVal, setInputVal] = useState('');
  const [options, setOptions]   = useState([]);
  const [loading, setLoading]   = useState(false);
  const debounce = useRef(null);

  const search = (q) => {
    clearTimeout(debounce.current);
    if (!q.trim()) { setOptions([]); return; }
    debounce.current = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await api.searchMerchants(q);
        setOptions(r.data.items || []);
      } catch {}
      finally { setLoading(false); }
    }, 350);
  };

  return (
    <Autocomplete
      multiple
      filterSelectedOptions
      options={options}
      value={selected}
      loading={loading}
      inputValue={inputVal}
      onInputChange={(_, v, reason) => {
        setInputVal(v);                    // always sync — keeps controlled state in sync after selection
        if (reason === 'reset') return;    // MUI cleared after select, don't trigger a new search
        search(v);
      }}
      onChange={(_, val) => onChange(val)}
      getOptionLabel={u => u.display_name || u.email || String(u.user_id)}
      isOptionEqualToValue={(a, b) => a.user_id === b.user_id}
      renderInput={(params) => (
        <TextField {...params} size="small" label="Search merchants by name or email"
          placeholder="Type to search…"
          InputProps={{ ...params.InputProps,
            startAdornment: (
              <>
                <Search sx={{ fontSize: 16, color: 'text.disabled', mr: 0.5, ml: 0.5 }} />
                {params.InputProps.startAdornment}
              </>
            ),
            endAdornment: (
              <>
                {loading && <CircularProgress size={14} sx={{ mr: 1 }} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }} />
      )}
      renderTags={(value, getTagProps) =>
        value.map((u, idx) => (
          <Chip key={u.user_id} size="small"
            label={u.display_name || u.email}
            {...getTagProps({ index: idx })}
            sx={{ fontWeight: 600, fontSize: 11 }} />
        ))
      }
      renderOption={(props, u) => (
        <ListItem {...props} key={u.user_id} dense>
          <ListItemText
            primary={<Typography variant="body2" fontWeight={600}>{u.display_name}</Typography>}
            secondary={<Typography variant="caption" color="text.disabled">{u.email}</Typography>} />
        </ListItem>
      )}
      noOptionsText={options.length === 0 && !loading ? 'Type to search merchants…' : 'No merchants found'}
      sx={{ mb: 1 }} />
  );
}

// ── MerchantCsvUpload — CSV/Excel file importer ───────────────────────────────
function MerchantCsvUpload({ onImport, show }) {
  const fileRef  = useRef(null);
  const [loading, setLoading] = useState(false);

  const downloadTemplate = () => {
    const csv = 'Name,Email,Mobile\nJohn Smith,john@example.com,9876543210\nPriya Sharma,priya@example.com,9123456789';
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'allowlist_template.csv';
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = '';
    const reader = new FileReader();
    reader.onload = async (ev) => {
      try {
        const text = ev.target.result;
        const lines = text.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
        const dataLines = lines[0]?.toLowerCase().includes('name') ? lines.slice(1) : lines;

        const rows = [];
        dataLines.forEach(line => {
          const cols = line.split(/[,\t]/).map(c => c.trim().replace(/^"|"$/g, ''));
          const email  = (cols[1] || '').toLowerCase().trim();
          const mobile = (cols[2] || '').trim();
          if (email || mobile) rows.push({ email, mobile });
        });

        if (!rows.length) { show('No data rows found in file', 'warning'); return; }

        setLoading(true);
        try {
          const r = await api.bulkLookupMerchants(rows);
          const { found = [], not_found = [] } = r.data;
          if (found.length) onImport(found);
          if (not_found.length === 0) {
            show(`Added ${found.length} merchant${found.length !== 1 ? 's' : ''} from file`);
          } else {
            const skipped = not_found.length;
            show(
              `Added ${found.length} merchant${found.length !== 1 ? 's' : ''}. ${skipped} not found: ${not_found.slice(0, 3).join(', ')}${skipped > 3 ? ` +${skipped - 3} more` : ''}`,
              found.length > 0 ? 'warning' : 'error',
            );
          }
        } finally {
          setLoading(false);
        }
      } catch {
        show('Could not parse file. Use CSV with columns: Name, Email, Mobile', 'error');
      }
    };
    reader.readAsText(file);
  };

  return (
    <Box display="flex" alignItems="center" gap={1} mt={1}>
      <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls,.txt"
        style={{ display: 'none' }} onChange={handleFile} />
      <Tooltip title="Download a sample CSV template">
        <Button size="small" variant="text" onClick={downloadTemplate}
          sx={{ fontSize: 11, px: 1, color: 'text.secondary', '&:hover': { color: 'primary.dark' } }}>
          Sample
        </Button>
      </Tooltip>
      <Tooltip title="Upload CSV — columns: Name, Email, Mobile">
        <Button size="small" variant="outlined" disabled={loading}
          startIcon={loading ? <CircularProgress size={12} /> : <UploadFileOutlined sx={{ fontSize: 16 }} />}
          onClick={() => fileRef.current?.click()}
          sx={{ borderRadius: 2, fontSize: 12, fontWeight: 600 }}>
          Upload CSV / Excel
        </Button>
      </Tooltip>
    </Box>
  );
}

// ── main ──────────────────────────────────────────────────────────────────────

export default function CouponsManagement() {
  const { hasPermission } = usePermissions();
  const canView       = hasPermission('Coupons', 'view');
  const canCreate     = hasPermission('Coupons', 'create');
  const canUpdate     = hasPermission('Coupons', 'update');
  const canActivate   = hasPermission('Coupons', 'activate');
  const canDeactivate = hasPermission('Coupons', 'deactivate');

  const [view, setView]     = useState('list');
  const [editId, setEditId] = useState(null);

  const [items, setItems]               = useState([]);
  const [total, setTotal]               = useState(0);
  const [page, setPage]                 = useState(0);
  const [perPage, setPerPage]           = useState(10);
  const [sortBy, setSortBy]             = useState('created_at');
  const [sortDir, setSortDir]           = useState('desc');
  const [listLoading, setListLoading]   = useState(false);
  const [search, setSearch]             = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const [form, setForm]         = useState(EMPTY_FORM);
  const [saving, setSaving]     = useState(false);
  const [couponErrors, setCouponErrors] = useState({});
  const [plans, setPlans]       = useState([]);
  const [addons, setAddons]     = useState([]);
  const [currencies, setCurrencies] = useState([]);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailItem, setDetailItem] = useState(null);

  const [usageOpen, setUsageOpen]     = useState(false);
  const [usageData, setUsageData]     = useState(null);
  const [usageLoading, setUsageLoading] = useState(false);

  const [snack, setSnack] = useState({ open: false, msg: '', sev: 'success' });
  const show = (msg, sev = 'success') => setSnack({ open: true, msg, sev });

  // ── list ──────────────────────────────────────────────────────────────────
  const loadItems = useCallback(async () => {
    setListLoading(true);
    try {
      const r = await api.getCoupons({
        search: search || undefined,
        status: statusFilter !== '' ? statusFilter : undefined,
        page: page + 1,
        per_page: perPage,
        sort_by: sortBy,
        sort_dir: sortDir,
      });
      setItems(r.data.items || []); setTotal(r.data.total || 0);
    } catch { show('Failed to load coupons', 'error'); }
    finally { setListLoading(false); }
  }, [search, statusFilter, page, perPage, sortBy, sortDir]);

  useEffect(() => { setPage(0); }, [search, statusFilter, sortBy, sortDir]);
  useEffect(() => { if (view === 'list') loadItems(); }, [view, loadItems]);

  useEffect(() => {
    api.getCurrencies().then(r => setCurrencies(r.data || [])).catch(() => {});
  }, []);

  const loadPlans = async () => {
    try {
      const [rPlans, rAddons] = await Promise.all([
        api.getPlans({ per_page: 100 }),
        api.getAddons({ per_page: 200, status: 1 }),
      ]);
      setPlans(rPlans.data.items || []);
      setAddons(rAddons.data.items || []);
    } catch {}
  };

  const handleExport = async () => {
    try {
      const res = await api.exportCoupons({
        status: statusFilter || undefined,
        search: search || undefined,
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `coupons_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { show('Export failed', 'error'); }
  };

  const openCreate = async () => {
    const now = new Date();
    const pad = n => String(n).padStart(2, '0');
    const nowLocal = `${now.getFullYear()}-${pad(now.getMonth()+1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
    setForm({ ...EMPTY_FORM, valid_from: nowLocal }); setEditId(null);
    await loadPlans();
    setView('form');
  };

  const openEdit = async (id) => {
    setEditId(id);
    await loadPlans();
    try {
      const r = await api.getCoupon(id);
      const c = r.data;
      setForm({
        code:                c.code || '',
        description:         c.description || '',
        discount_type:       c.discount_type || 'percent',
        discount_value:      String(c.discount_value || ''),
        currency_code:       c.currency_code || 'INR',
        valid_from:          c.valid_from ? c.valid_from.slice(0, 16) : '',
        valid_to:            c.valid_to   ? c.valid_to.slice(0, 16)   : '',
        max_usage_count:     c.max_usage_count != null ? String(c.max_usage_count) : '',
        per_user_limit:      String(c.per_user_limit || 1),
        first_invoice_only:  c.first_invoice_only || false,
        scopes:              c.scopes || [],
        allowed_merchant_ids: (c.allowed_merchants || []).map(m => m.user_id),
        selectedMerchants:   [],  // resolved below
      });
      // Resolve saved merchant IDs → user objects for the multi-select
      if (c.allowed_merchants?.length > 0) {
        Promise.all(c.allowed_merchants.map(m =>
          api.searchMerchants(String(m.user_id))
            .then(r => (r.data.items || []).find(u => u.user_id === m.user_id))
            .catch(() => null)
        )).then(resolved => {
          const found = resolved.filter(Boolean);
          setForm(f => ({ ...f, selectedMerchants: found, allowed_merchant_ids: found.map(u => u.user_id) }));
        });
      }
    } catch { show('Failed to load coupon', 'error'); }
    setView('form');
  };

  const handleSave = async () => {
    const errs = {};
    if (!form.code.trim())
      errs.code = 'Coupon Code is required';
    else if (!/^[A-Z0-9_-]+$/.test(form.code.trim()))
      errs.code = 'Only A–Z, 0–9, underscore, and hyphen allowed';
    else if (form.code.trim().length > 100)
      errs.code = 'Max 100 characters';
    if (form.discount_value === '' || form.discount_value === null)
      errs.discount_value = 'Discount value is required';
    else if (Number(form.discount_value) <= 0)
      errs.discount_value = 'Must be greater than 0';
    else if (form.discount_type === 'percent' && Number(form.discount_value) > 100)
      errs.discount_value = 'Percentage cannot exceed 100';
    if (!form.valid_from) errs.valid_from = 'Valid From date is required';
    if (!form.valid_to)   errs.valid_to   = 'Valid Until date is required';
    else if (form.valid_from && form.valid_to <= form.valid_from)
      errs.valid_to = 'Must be after Valid From';
    if (Object.keys(errs).length > 0) { setCouponErrors(errs); return; }
    setCouponErrors({});
    setSaving(true);
    // eslint-disable-next-line no-unused-vars
    const { selectedMerchants: _sm, ...formData } = form;
    const payload = {
      ...formData,
      code:            form.code.toUpperCase().trim(),
      discount_value:  Number(form.discount_value),
      max_usage_count: form.max_usage_count ? Number(form.max_usage_count) : null,
      per_user_limit:  Number(form.per_user_limit || 1),
    };
    try {
      if (editId) {
        await api.updateCoupon(editId, payload);
        show('Coupon updated');
      } else {
        await api.createCoupon(payload);
        show('Coupon created');
      }
      setView('list');
    } catch (e) { show(e.response?.data?.message || 'Save failed', 'error'); }
    finally { setSaving(false); }
  };

  const toggleStatus = async (c) => {
    try {
      if (c.status === 1) { await api.deactivateCoupon(c.id); show('Coupon deactivated'); }
      else                { await api.activateCoupon(c.id);   show('Coupon activated');   }
      loadItems();
    } catch (e) { show(e.response?.data?.message || 'Action failed', 'error'); }
  };

  const openDetail = async (id) => {
    try {
      const r = await api.getCoupon(id);
      setDetailItem(r.data); setDetailOpen(true);
    } catch { show('Failed to load coupon', 'error'); }
  };

  const openUsageHistory = async (id) => {
    setUsageData(null);
    setUsageOpen(true);
    setUsageLoading(true);
    try {
      const r = await api.getCouponUsage(id);
      setUsageData(r.data);
    } catch { show('Failed to load usage history', 'error'); setUsageOpen(false); }
    finally { setUsageLoading(false); }
  };


  if (!canView) return (
    <Box sx={{ p: 4, textAlign: 'center' }}>
      <Typography color="text.secondary">You don't have permission to view coupons.</Typography>
    </Box>
  );

  // ══════════════════════════════════════════════════════════════════════════
  // FORM VIEW
  // ══════════════════════════════════════════════════════════════════════════
  if (view === 'form') {
    const setF = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

    return (
      <Box sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center"
          sx={{ position: 'sticky', top: 0, zIndex: 10, bgcolor: 'background.paper',
            borderBottom: '1px solid', borderColor: 'divider',
            py: 1.5, mb: 3, mx: -3, px: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
          <Box display="flex" alignItems="center" gap={1.5}>
            <Tooltip title="Back">
              <IconButton size="small" onClick={() => setView('list')}
                sx={{ border: '1px solid', borderColor: 'divider', '&:hover': { bgcolor: 'grey.100' } }}>
                <ArrowBack fontSize="small" />
              </IconButton>
            </Tooltip>
            <Box>
              <Typography variant="h6" fontWeight={800} letterSpacing={-0.3} lineHeight={1.2}>
                {editId ? `Edit — ${form.code || '…'}` : 'New Coupon'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {editId ? 'Update coupon details, scope, and restrictions' : 'Create a discount coupon for subscriptions'}
              </Typography>
            </Box>
          </Box>
          <Button variant="contained"
            startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <Save />}
            onClick={handleSave} disabled={saving}
            sx={{ borderRadius: 2, fontWeight: 700, minWidth: 140,
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.2)' } }}>
            {saving ? 'Saving…' : 'Save Coupon'}
          </Button>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

          {/* Basics */}
          <Accordion defaultExpanded disableGutters elevation={0} sx={ACCORDION_SX}>
            <AccordionSummary expandIcon={<ExpandMore />} sx={{ px: 3, py: 1.5, minHeight: 52 }}>
              <Typography fontWeight={700}>Basics</Typography>
            </AccordionSummary>
            <Divider />
            <AccordionDetails sx={{ p: 3 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2.5 }}>
                <TextField label="Coupon Code *" size="small" fullWidth
                  value={form.code}
                  error={!!couponErrors.code}
                  helperText={couponErrors.code || 'Auto-uppercased · A–Z, 0–9, _ and -'}
                  onChange={e => {
                    const val = e.target.value.toUpperCase().replace(/\s/g, '');
                    setF('code', val);
                    if (couponErrors.code) setCouponErrors(p => ({ ...p, code: '' }));
                  }}
                  onBlur={() => {
                    const v = form.code.trim();
                    let err = '';
                    if (!v) err = 'Coupon Code is required';
                    else if (!/^[A-Z0-9_-]+$/.test(v)) err = 'Only A–Z, 0–9, underscore, and hyphen allowed';
                    else if (v.length > 100) err = 'Max 100 characters';
                    setCouponErrors(p => ({ ...p, code: err }));
                  }}
                  placeholder="e.g. LAUNCH50"
                  inputProps={{ style: { fontFamily: 'monospace', fontWeight: 700, letterSpacing: 1 } }} />
                <TextField label="Description" size="small" fullWidth
                  value={form.description}
                  onChange={e => setF('description', e.target.value)}
                  placeholder="Internal note about this coupon" />
              </Box>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel>Discount Type *</InputLabel>
                  <Select label="Discount Type *" value={form.discount_type}
                    onChange={e => setF('discount_type', e.target.value)}>
                    {DISCOUNT_TYPES.map(t => (
                      <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField label={
                  form.discount_type === 'percent'     ? 'Discount % *' :
                  form.discount_type === 'token_grant' ? 'Tokens to Grant *' : 'Discount Amount *'
                } type="number" size="small" sx={{ width: 160 }}
                  value={form.discount_value}
                  error={!!couponErrors.discount_value}
                  helperText={couponErrors.discount_value || ' '}
                  onChange={e => {
                    setF('discount_value', e.target.value);
                    if (couponErrors.discount_value) setCouponErrors(p => ({ ...p, discount_value: '' }));
                  }}
                  onBlur={() => {
                    let err = '';
                    if (form.discount_value === '' || form.discount_value === null) err = 'Required';
                    else if (Number(form.discount_value) <= 0) err = 'Must be greater than 0';
                    else if (form.discount_type === 'percent' && Number(form.discount_value) > 100) err = 'Cannot exceed 100%';
                    setCouponErrors(p => ({ ...p, discount_value: err }));
                  }}
                  inputProps={{ min: 0, max: form.discount_type === 'percent' ? 100 : undefined, step: 0.01 }} />
                {form.discount_type === 'fixed_amount' && (
                  <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel>Currency *</InputLabel>
                    <Select label="Currency *" value={form.currency_code}
                      onChange={e => setF('currency_code', e.target.value)}>
                      {currencies.length === 0
                        ? <MenuItem value="INR">INR</MenuItem>
                        : currencies.map(c => (
                            <MenuItem key={c.code} value={c.code}>{c.code} — {c.display_name}</MenuItem>
                          ))
                      }
                    </Select>
                  </FormControl>
                )}
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Validity & Limits */}
          <Accordion defaultExpanded disableGutters elevation={0} sx={ACCORDION_SX}>
            <AccordionSummary expandIcon={<ExpandMore />} sx={{ px: 3, py: 1.5, minHeight: 52 }}>
              <Typography fontWeight={700}>Validity & Usage Limits</Typography>
            </AccordionSummary>
            <Divider />
            <AccordionDetails sx={{ p: 3 }}>
              {/* Date range */}
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 3 }}>
                <TextField label="Valid From *" type="datetime-local" size="small" fullWidth
                  value={form.valid_from}
                  error={!!couponErrors.valid_from}
                  helperText={couponErrors.valid_from || ' '}
                  onChange={e => { setF('valid_from', e.target.value); if (couponErrors.valid_from) setCouponErrors(p => ({ ...p, valid_from: '' })); }}
                  onBlur={() => setCouponErrors(p => ({ ...p, valid_from: form.valid_from ? '' : 'Valid From date is required' }))}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{ endAdornment: (
                    <InputAdornment position="end" sx={{ pointerEvents: 'none' }}>
                      <CalendarMonth sx={{ fontSize: 18, color: 'text.disabled' }} />
                    </InputAdornment>
                  )}}
                  sx={DATE_SX} />
                <TextField label="Valid Until *" type="datetime-local" size="small" fullWidth
                  value={form.valid_to}
                  error={!!couponErrors.valid_to}
                  helperText={couponErrors.valid_to || ' '}
                  onChange={e => { setF('valid_to', e.target.value); if (couponErrors.valid_to) setCouponErrors(p => ({ ...p, valid_to: '' })); }}
                  onBlur={() => {
                    let err = '';
                    if (!form.valid_to) err = 'Valid Until date is required';
                    else if (form.valid_from && form.valid_to <= form.valid_from) err = 'Must be after Valid From';
                    setCouponErrors(p => ({ ...p, valid_to: err }));
                  }}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{ endAdornment: (
                    <InputAdornment position="end" sx={{ pointerEvents: 'none' }}>
                      <CalendarMonth sx={{ fontSize: 18, color: 'text.disabled' }} />
                    </InputAdornment>
                  )}}
                  sx={DATE_SX} />
              </Box>

              {/* Usage limits */}
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2, alignItems: 'start' }}>
                <TextField label="Max Total Uses" type="number" size="small" fullWidth
                  value={form.max_usage_count} onChange={e => setF('max_usage_count', e.target.value)}
                  placeholder="∞ Unlimited" inputProps={{ min: 1 }}
                  helperText="Leave blank for unlimited"
                  sx={NUM_NO_SPIN} />
                <TextField label="Per User Limit *" type="number" size="small" fullWidth
                  value={form.per_user_limit} onChange={e => setF('per_user_limit', e.target.value)}
                  inputProps={{ min: 1 }}
                  helperText="Max redemptions per merchant"
                  sx={NUM_NO_SPIN} />
                <Box sx={{
                  display: 'flex', alignItems: 'center', gap: 1.5,
                  bgcolor: form.first_invoice_only ? 'primary.50' : 'grey.50',
                  border: '1px solid', borderColor: form.first_invoice_only ? 'primary.200' : 'divider',
                  borderRadius: 2, px: 2, py: 1.25, cursor: 'pointer', transition: 'all 0.2s',
                  '&:hover': { borderColor: 'primary.300' },
                }} onClick={() => setF('first_invoice_only', !form.first_invoice_only)}>
                  <Switch size="small" checked={form.first_invoice_only}
                    onChange={e => { e.stopPropagation(); setF('first_invoice_only', e.target.checked); }}
                    color="primary" />
                  <Box>
                    <Typography variant="body2" fontWeight={600} lineHeight={1.3}
                      color={form.first_invoice_only ? 'primary.main' : 'text.primary'}>
                      First invoice only
                    </Typography>
                    <Typography variant="caption" color="text.secondary" lineHeight={1.2}>
                      Apply on first payment only
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Scope — searchable multi-select plans + add-ons */}
          <Accordion defaultExpanded disableGutters elevation={0} sx={ACCORDION_SX}>
            <AccordionSummary expandIcon={<ExpandMore />} sx={{ px: 3, py: 1.5, minHeight: 52 }}>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography fontWeight={700}>Scope</Typography>
                {(() => {
                  const planCount  = form.scopes.filter(s => s.scope_type === 'plan').length;
                  const addonCount = form.scopes.filter(s => s.scope_type === 'addon').length;
                  const scopeLabel = (!planCount && !addonCount)
                    ? 'All plans & add-ons'
                    : [planCount  && `${planCount} plan${planCount > 1 ? 's' : ''}`,
                       addonCount && `${addonCount} add-on${addonCount > 1 ? 's' : ''}`]
                      .filter(Boolean).join(', ');
                  return (
                    <Chip size="small"
                      label={scopeLabel}
                      color={form.scopes.length === 0 ? 'default' : 'primary'}
                      sx={{ fontWeight: 600, fontSize: 11, height: 20, '& .MuiChip-label': { px: 1 } }} />
                  );
                })()}
              </Box>
            </AccordionSummary>
            <Divider />
            <AccordionDetails sx={{ p: 3 }}>
              <Typography variant="body2" color="text.secondary" mb={2.5}>
                Leave empty to apply to <strong>all plans & add-ons</strong>. Select specific items below to restrict this coupon.
              </Typography>

              {/* Plans */}
              <Autocomplete
                multiple
                disableCloseOnSelect
                filterSelectedOptions
                options={plans}
                getOptionLabel={p => `${p.display_name} (${p.plan_code})`}
                filterOptions={(opts, { inputValue }) => {
                  const q = inputValue.toLowerCase().trim();
                  if (!q) return opts;
                  return opts.filter(p =>
                    p.display_name?.toLowerCase().includes(q) ||
                    p.plan_code?.toLowerCase().includes(q)
                  );
                }}
                value={plans.filter(p => form.scopes.some(s => s.scope_type === 'plan' && s.scope_id === p.id))}
                onChange={(_, selected) => {
                  const addonScopes = form.scopes.filter(s => s.scope_type === 'addon');
                  setF('scopes', [
                    ...selected.map(p => ({ scope_type: 'plan', scope_id: p.id })),
                    ...addonScopes,
                  ]);
                }}
                isOptionEqualToValue={(opt, val) => opt.id === val.id}
                renderInput={(params) => (
                  <TextField {...params} size="small" label="Select plans"
                    placeholder="Search or scroll to pick plans…"
                    InputProps={{ ...params.InputProps,
                      startAdornment: (
                        <>
                          <Search sx={{ fontSize: 16, color: 'text.disabled', mr: 0.5, ml: 0.5 }} />
                          {params.InputProps.startAdornment}
                        </>
                      )
                    }} />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((p, idx) => (
                    <Chip key={p.id} size="small" label={p.display_name}
                      {...getTagProps({ index: idx })}
                      sx={{ fontWeight: 600, fontSize: 11 }} />
                  ))
                }
                renderOption={(props, p) => (
                  <ListItem {...props} key={p.id} dense>
                    <ListItemText
                      primary={<Typography variant="body2" fontWeight={600}>{p.display_name}</Typography>}
                      secondary={<Typography variant="caption" color="text.disabled">{p.plan_code}</Typography>} />
                  </ListItem>
                )}
                noOptionsText={plans.length === 0 ? 'Loading plans…' : 'No plans found'}
                sx={{ mb: 2 }} />

              {/* Add-ons */}
              <Autocomplete
                multiple
                disableCloseOnSelect
                filterSelectedOptions
                options={addons}
                getOptionLabel={a => `${a.display_name} (${a.addon_code})`}
                filterOptions={(opts, { inputValue }) => {
                  const q = inputValue.toLowerCase().trim();
                  if (!q) return opts;
                  return opts.filter(a =>
                    a.display_name?.toLowerCase().includes(q) ||
                    a.addon_code?.toLowerCase().includes(q)
                  );
                }}
                value={addons.filter(a => form.scopes.some(s => s.scope_type === 'addon' && s.scope_id === a.id))}
                onChange={(_, selected) => {
                  const planScopes = form.scopes.filter(s => s.scope_type === 'plan');
                  setF('scopes', [
                    ...planScopes,
                    ...selected.map(a => ({ scope_type: 'addon', scope_id: a.id })),
                  ]);
                }}
                isOptionEqualToValue={(opt, val) => opt.id === val.id}
                renderInput={(params) => (
                  <TextField {...params} size="small" label="Select add-ons"
                    placeholder="Search or scroll to pick add-ons…"
                    InputProps={{ ...params.InputProps,
                      startAdornment: (
                        <>
                          <Search sx={{ fontSize: 16, color: 'text.disabled', mr: 0.5, ml: 0.5 }} />
                          {params.InputProps.startAdornment}
                        </>
                      )
                    }} />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((a, idx) => (
                    <Chip key={a.id} size="small" label={a.display_name}
                      {...getTagProps({ index: idx })}
                      sx={{ fontWeight: 600, fontSize: 11 }} />
                  ))
                }
                renderOption={(props, a) => (
                  <ListItem {...props} key={a.id} dense>
                    <ListItemText
                      primary={<Typography variant="body2" fontWeight={600}>{a.display_name}</Typography>}
                      secondary={<Typography variant="caption" color="text.disabled">{a.addon_code}</Typography>} />
                  </ListItem>
                )}
                noOptionsText={addons.length === 0 ? 'Loading add-ons…' : 'No add-ons found'}
                sx={{ mb: 1.5 }} />

              {form.scopes.length === 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary', mt: 1 }}>
                  <InfoOutlined sx={{ fontSize: 15 }} />
                  <Typography variant="caption">No restriction set — applies to all plans and add-ons.</Typography>
                </Box>
              )}
            </AccordionDetails>
          </Accordion>

          {/* Merchant Allowlist — searchable multi-select + CSV/Excel upload */}
          <Accordion disableGutters elevation={0} sx={ACCORDION_SX}>
            <AccordionSummary expandIcon={<ExpandMore />} sx={{ px: 3, py: 1.5, minHeight: 52 }}>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography fontWeight={700}>Merchant Allowlist</Typography>
                <Chip size="small"
                  label={form.selectedMerchants?.length > 0
                    ? `${form.selectedMerchants.length} merchant${form.selectedMerchants.length !== 1 ? 's' : ''}`
                    : 'All merchants'}
                  color={form.selectedMerchants?.length > 0 ? 'primary' : 'default'}
                  sx={{ fontWeight: 600, fontSize: 11, height: 20, '& .MuiChip-label': { px: 1 } }} />
              </Box>
            </AccordionSummary>
            <Divider />
            <AccordionDetails sx={{ p: 3 }}>
              <Typography variant="body2" color="text.secondary" mb={2.5}>
                Leave empty to allow <strong>all merchants</strong>. Search and select specific merchants, or upload a CSV/Excel file.
              </Typography>

              {/* Search & select */}
              <MerchantMultiSelect
                selected={form.selectedMerchants || []}
                onChange={merchants => {
                  setF('selectedMerchants', merchants);
                  setF('allowed_merchant_ids', merchants.map(m => m.user_id));
                }} />

              {/* CSV / Excel upload */}
              <MerchantCsvUpload
                show={show}
                onImport={newMerchants => {
                  const merged = [...(form.selectedMerchants || [])];
                  newMerchants.forEach(m => { if (!merged.some(x => x.user_id === m.user_id)) merged.push(m); });
                  setF('selectedMerchants', merged);
                  setF('allowed_merchant_ids', merged.map(m => m.user_id));
                }} />

              {form.selectedMerchants?.length === 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary', mt: 1 }}>
                  <InfoOutlined sx={{ fontSize: 15 }} />
                  <Typography variant="caption">No restriction — all merchants can use this coupon.</Typography>
                </Box>
              )}
            </AccordionDetails>
          </Accordion>

        </Box>

        <Snackbar open={snack.open} autoHideDuration={4000}
          onClose={() => setSnack(s => ({ ...s, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
          <Alert severity={snack.sev} onClose={() => setSnack(s => ({ ...s, open: false }))}>{snack.msg}</Alert>
        </Snackbar>
      </Box>
    );
  }

  // ══════════════════════════════════════════════════════════════════════════
  // LIST VIEW
  // ══════════════════════════════════════════════════════════════════════════
  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start"
        mb={3} pb={2.5}
        sx={{ position: 'sticky', top: 0, zIndex: 10, bgcolor: 'background.paper',
          borderBottom: '1px solid', borderColor: 'divider',
          mx: -3, px: 3, pt: 2.5,
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
        <Box>
          <Typography variant="h5" fontWeight={800} letterSpacing={-0.5} gutterBottom>Coupons</Typography>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="body2" color="text.secondary">
              Discount codes for subscription plans and add-ons
            </Typography>
            {!listLoading && total > 0 && (
              <>
                <Box sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: 'text.disabled' }} />
                <Typography variant="body2" color="text.disabled">
                  {total} coupon{total !== 1 ? 's' : ''}
                </Typography>
              </>
            )}
          </Box>
        </Box>
        <Box display="flex" gap={1.5} alignItems="center" mt={0.5}>
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={loadItems}
              sx={{ border: '1px solid', borderColor: 'divider', '&:hover': { bgcolor: 'grey.50' } }}>
              <Refresh fontSize="small" />
            </IconButton>
          </Tooltip>
          <Button size="medium" variant="outlined" startIcon={<FileDownloadOutlined />} onClick={handleExport}
            sx={{ borderRadius: 2, fontWeight: 600, textTransform: 'none' }}>
            Export CSV
          </Button>
          {canCreate && (
            <Button variant="contained" startIcon={<Add />} onClick={openCreate}
              sx={{ borderRadius: 2, fontWeight: 700,
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.2)' } }}>
              New Coupon
            </Button>
          )}
        </Box>
      </Box>

      {/* Filters */}
      <Paper variant="outlined" sx={{ borderRadius: 2, mb: 2.5, overflow: 'hidden' }}>
        <Box display="flex" gap={1.5} px={2} py={1.5} alignItems="center" flexWrap="wrap" sx={{ bgcolor: 'grey.50' }}>
          <TuneRounded sx={{ fontSize: 18, color: 'text.disabled' }} />
          <TextField size="small" placeholder="Search code…" value={search}
            onChange={e => setSearch(e.target.value)} sx={{ width: 220 }}
            InputProps={{ startAdornment: <InputAdornment position="start">
              <Search sx={{ fontSize: 17, color: 'text.disabled' }} /></InputAdornment>,
              sx: { borderRadius: 2, bgcolor: 'white' } }} />
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Status</InputLabel>
            <Select label="Status" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
              sx={{ borderRadius: 2, bgcolor: 'white' }}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="1">Active</MenuItem>
              <MenuItem value="0">Inactive</MenuItem>
            </Select>
          </FormControl>
          {(search || statusFilter !== '') && (
            <Button size="small" onClick={() => { setSearch(''); setStatusFilter(''); }}
              sx={{ color: 'error.main', fontWeight: 600, fontSize: 12, '&:hover': { bgcolor: 'error.50' } }}>
              Clear
            </Button>
          )}
          <Typography variant="caption" color="text.disabled" sx={{ ml: 'auto' }}>
            {listLoading ? 'Loading…' : `${total} coupon${total !== 1 ? 's' : ''}`}
          </Typography>
        </Box>
      </Paper>

      {/* Table */}
      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...TH, pl: 2.5, width: 40 }}>#</TableCell>
                <TableCell sx={{ ...TH, width: 150 }}>
                  <SortTH label="Code" field="code" sortBy={sortBy} sortDir={sortDir} onSort={col => { setSortBy(col); setSortDir(d => col === sortBy ? (d==='asc'?'desc':'asc') : 'asc'); }} />
                </TableCell>
                <TableCell sx={TH}>Description</TableCell>
                <TableCell sx={{ ...TH, width: 140 }}>Discount</TableCell>
                <TableCell sx={{ ...TH, width: 200 }}>
                  <SortTH label="Validity" field="valid_from" sortBy={sortBy} sortDir={sortDir} onSort={col => { setSortBy(col); setSortDir(d => col === sortBy ? (d==='asc'?'desc':'asc') : 'asc'); }} />
                </TableCell>
                <TableCell sx={{ ...TH, width: 110 }}>
                  <SortTH label="Usage" field="current_usage" sortBy={sortBy} sortDir={sortDir} onSort={col => { setSortBy(col); setSortDir(d => col === sortBy ? (d==='asc'?'desc':'asc') : 'desc'); }} />
                </TableCell>
                <TableCell sx={{ ...TH, width: 90 }}>Status</TableCell>
                <TableCell sx={{ ...TH, textAlign: 'right', pr: 2, width: 120 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {listLoading
                ? Array.from({ length: 5 }).map((_, i) => (
                    <TableRow key={i}>
                      {Array.from({ length: 8 }).map((__, j) => (
                        <TableCell key={j} sx={{ py: 1.5 }}><Skeleton height={20} /></TableCell>
                      ))}
                    </TableRow>
                  ))
                : items.length === 0
                ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 8, border: 0 }}>
                      <LocalOfferOutlined sx={{ fontSize: 40, color: 'text.disabled', mb: 1.5, display: 'block', mx: 'auto' }} />
                      <Typography variant="body1" fontWeight={600} color="text.secondary" mb={0.5}>
                        {search || statusFilter !== '' ? 'No results' : 'No coupons yet'}
                      </Typography>
                      <Typography variant="body2" color="text.disabled" mb={2}>
                        {search || statusFilter !== ''
                          ? 'Try adjusting your search or status filter'
                          : 'Create a coupon code to offer discounts to merchants'}
                      </Typography>
                      {!search && statusFilter === '' && canCreate && (
                        <Button variant="outlined" startIcon={<Add />} onClick={openCreate}
                          size="small" sx={{ borderRadius: 2, fontWeight: 600 }}>
                          Create first coupon
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                )
                : items.map((c, i) => (
                    <TableRow key={c.id} hover
                      sx={{ '&:last-child td': { borderBottom: 'none' } }}>
                      <TableCell sx={{ pl: 2.5, color: 'text.disabled', fontSize: 13 }}>{page * perPage + i + 1}</TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={800} fontSize={13}
                          fontFamily="'Roboto Mono', monospace" letterSpacing={0.5}>
                          {c.code}
                        </Typography>
                        {c.scope_count > 0 && (
                          <Typography variant="caption" color="text.disabled">
                            {c.scope_count} scope rule{c.scope_count !== 1 ? 's' : ''}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ py: 1.5, maxWidth: 220 }}>
                        <Tooltip title={c.description || ''} placement="top" arrow disableHoverListener={!c.description}>
                          <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 220 }}>
                            {c.description || '—'}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={700}>{fmtDiscount(c)}</Typography>
                        <Typography variant="caption" color="text.disabled" sx={{ textTransform: 'capitalize' }}>
                          {c.discount_type.replace(/_/g, ' ')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontSize={12}>{fmt(c.valid_from)}</Typography>
                        <Typography variant="caption" color="text.disabled">to {fmt(c.valid_to)}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {c.current_usage}{c.max_usage_count ? ` / ${c.max_usage_count}` : ' / ∞'}
                        </Typography>
                      </TableCell>
                      <TableCell><StatusChip status={c.status} /></TableCell>
                      <TableCell align="right" sx={{ pr: 2 }}>
                        <Box display="flex" gap={0.5} justifyContent="flex-end">
                          <Tooltip title="View detail">
                            <IconButton size="small" onClick={() => openDetail(c.id)} sx={BTN_PRIMARY}>
                              <Visibility sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Usage history">
                            <IconButton size="small" onClick={() => openUsageHistory(c.id)} sx={BTN_PRIMARY}>
                              <HistoryOutlined sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                          {canUpdate && (
                            <Tooltip title="Edit">
                              <IconButton size="small" onClick={() => openEdit(c.id)} sx={BTN_PRIMARY}>
                                <EditOutlined sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          {c.status === 1 && canDeactivate && (
                            <Tooltip title="Deactivate">
                              <IconButton size="small" onClick={() => toggleStatus(c)} sx={BTN_ERROR}>
                                <BlockOutlined sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          {c.status === 0 && canActivate && (
                            <Tooltip title="Activate">
                              <IconButton size="small" onClick={() => toggleStatus(c)} sx={BTN_SUCCESS}>
                                <CheckCircleOutline sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
              }
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={(_, p) => setPage(p)}
        rowsPerPage={perPage}
        onRowsPerPageChange={e => { setPerPage(+e.target.value); setPage(0); }}
        rowsPerPageOptions={[10, 25, 50]}
        sx={{ borderTop: '1px solid', borderColor: 'divider', mt: 0.5 }}
      />

      {/* Detail dialog */}
      {detailItem && (
        <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} fullWidth maxWidth="sm"
          PaperProps={{ sx: { borderRadius: 3 } }}>
          <DialogTitle sx={{ fontWeight: 800, pb: 1 }}>
            <Typography component="span" fontFamily="'Roboto Mono', monospace"
              fontWeight={900} fontSize={20} letterSpacing={1}>
              {detailItem.code}
            </Typography>
          </DialogTitle>
          <Divider />
          <DialogContent sx={{ pt: 2.5 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
              {[
                ['Discount',     fmtDiscount(detailItem)],
                ['Type',         detailItem.discount_type.replace(/_/g, ' ')],
                ['Valid From',   fmt(detailItem.valid_from)],
                ['Valid Until',  fmt(detailItem.valid_to)],
                ['Total Uses',   `${detailItem.current_usage} / ${detailItem.max_usage_count ?? '∞'}`],
                ['Per User',     detailItem.per_user_limit],
                ['First Invoice Only', detailItem.first_invoice_only ? 'Yes' : 'No'],
                ['Status',       detailItem.status === 1 ? 'Active' : 'Inactive'],
              ].map(([lbl, val]) => (
                <Box key={lbl}>
                  <Typography variant="caption" fontWeight={700} color="text.secondary"
                    letterSpacing={0.6} sx={{ textTransform: 'uppercase', display: 'block' }}>{lbl}</Typography>
                  <Typography variant="body2" fontWeight={600} mt={0.2} sx={{ textTransform: 'capitalize' }}>
                    {String(val)}
                  </Typography>
                </Box>
              ))}
            </Box>
            {detailItem.description && (
              <Box mb={2}>
                <Typography variant="caption" fontWeight={700} color="text.secondary"
                  letterSpacing={0.6} sx={{ textTransform: 'uppercase', display: 'block', mb: 0.5 }}>Notes</Typography>
                <Typography variant="body2">{detailItem.description}</Typography>
              </Box>
            )}
            {detailItem.scopes?.length > 0 && (
              <Box>
                <Typography variant="caption" fontWeight={700} color="text.secondary"
                  letterSpacing={0.6} sx={{ textTransform: 'uppercase', display: 'block', mb: 1 }}>Scope</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8 }}>
                  {detailItem.scopes.map((s, i) => (
                    <Chip key={i} size="small"
                      label={`${s.scope_type}${s.scope_id ? ` #${s.scope_id}` : ''}`}
                      sx={{ fontWeight: 600, fontSize: 11, textTransform: 'capitalize' }} />
                  ))}
                </Box>
              </Box>
            )}
          </DialogContent>
          <Divider />
          <DialogActions sx={{ px: 3, py: 1.5 }}>
            <Button onClick={() => setDetailOpen(false)} sx={{ borderRadius: 2 }}>Close</Button>
            {canUpdate && (
              <Button variant="contained" sx={{ borderRadius: 2, fontWeight: 700 }}
                onClick={() => { setDetailOpen(false); openEdit(detailItem.id); }}>
                Edit
              </Button>
            )}
          </DialogActions>
        </Dialog>
      )}

      {/* ── Usage History Dialog ── */}
      <Dialog open={usageOpen} onClose={() => setUsageOpen(false)} fullWidth maxWidth="md"
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <Box sx={{ px: 3, py: 2, bgcolor: '#f3eeff', borderBottom: '1px solid #e9d9fc',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box display="flex" alignItems="center" gap={1}>
            <HistoryOutlined sx={{ color: '#6318C6', fontSize: 20 }} />
            <Box>
              <Typography fontWeight={800} sx={{ color: '#6318C6', lineHeight: 1.2 }}>
                Usage History
              </Typography>
              {usageData && (
                <Typography sx={{ fontSize: 12, color: '#7c3aed' }}>
                  {usageData.coupon_code} · {usageData.total_usage} redemption{usageData.total_usage !== 1 ? 's' : ''}
                </Typography>
              )}
            </Box>
          </Box>
          <IconButton size="small" onClick={() => setUsageOpen(false)}>
            <CloseRounded sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>

        <DialogContent sx={{ p: 0 }}>
          {usageLoading ? (
            <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {[1, 2, 3].map(i => <Skeleton key={i} variant="rectangular" height={52} sx={{ borderRadius: 1 }} />)}
            </Box>
          ) : usageData?.redemptions?.length === 0 ? (
            <Box sx={{ p: 5, textAlign: 'center' }}>
              <HistoryOutlined sx={{ fontSize: 48, color: '#d1d5db', mb: 1 }} />
              <Typography color="text.secondary" fontWeight={600}>No redemptions yet</Typography>
              <Typography variant="body2" color="text.secondary" mt={0.5}>
                This coupon has not been used by any merchant.
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f9fafb' }}>
                    <TableCell sx={{ fontWeight: 700, fontSize: 12, color: '#6b7280' }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: 12, color: '#6b7280' }}>Merchant</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: 12, color: '#6b7280' }}>Plan</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: 12, color: '#6b7280' }}>Invoice</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: 12, color: '#6b7280' }}>Discount Applied</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: 12, color: '#6b7280' }}>Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(usageData?.redemptions || []).map((r, idx) => {
                    const sym    = r.currency_code === 'USD' ? '$' : '₹';
                    const locale = r.currency_code === 'USD' ? 'en-US' : 'en-IN';
                    return (
                      <TableRow key={r.id} hover>
                        <TableCell sx={{ fontSize: 12, color: '#9ca3af' }}>{idx + 1}</TableCell>
                        <TableCell>
                          <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>
                            {r.merchant_name}
                          </Typography>
                          <Typography sx={{ fontSize: 11, color: '#6b7280' }}>{r.merchant_email}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography sx={{ fontSize: 12, color: '#374151' }}>
                            {r.plan_name || <span style={{ color: '#d1d5db' }}>—</span>}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {r.invoice_ref
                            ? <Chip label={r.invoice_ref} size="small"
                                sx={{ fontSize: 11, fontWeight: 700, bgcolor: '#ede9fe', color: '#6318C6', height: 22 }} />
                            : <Typography sx={{ fontSize: 12, color: '#9ca3af' }}>Pending payment</Typography>
                          }
                        </TableCell>
                        <TableCell align="right">
                          <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#16a34a',
                            fontFamily: "'Roboto Mono', monospace" }}>
                            −{sym}{Number(r.discount_applied).toLocaleString(locale, { minimumFractionDigits: 2 })}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography sx={{ fontSize: 12, color: '#6b7280' }}>
                            {r.created_at ? new Date(r.created_at).toLocaleDateString('en-IN',
                              { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>

        {usageData && usageData.total_usage > 0 && (
          <Box sx={{ px: 3, py: 1.5, bgcolor: '#f9fafb', borderTop: '1px solid #e5e7eb',
            display: 'flex', justifyContent: 'flex-end', gap: 3 }}>
            <Typography sx={{ fontSize: 13, color: '#6b7280' }}>
              Total redemptions: <strong>{usageData.total_usage}</strong>
            </Typography>
            <Typography sx={{ fontSize: 13, color: '#16a34a', fontWeight: 700 }}>
              {(() => {
                const total  = (usageData.redemptions || []).reduce((s, r) => s + (r.discount_applied || 0), 0);
                const first  = usageData.redemptions?.[0];
                const sym    = first?.currency_code === 'USD' ? '$' : '₹';
                const locale = first?.currency_code === 'USD' ? 'en-US' : 'en-IN';
                return `Total discount given: ${sym}${total.toLocaleString(locale, { minimumFractionDigits: 2 })}`;
              })()}
            </Typography>
          </Box>
        )}
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={4000}
        onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snack.sev} onClose={() => setSnack(s => ({ ...s, open: false }))}>{snack.msg}</Alert>
      </Snackbar>
    </Box>
  );
}
